/**
 * Max Priorities Pyramid Types (Book Version)
 * 
 * Based on the Managing Priorities book, the pyramid is a 3-row by N-column grid:
 * - Rows (Time Horizons): Get House in Order, Reinforce Foundation, Create the Future
 * - Columns (Strategic Themes): Configurable (e.g., Process Automation, Tech Debt, Defects, Other Priorities)
 * 
 * The pyramid structure helps visualize:
 * - Directional alignment (North Star at top)
 * - Feedback loops between near-term and long-term priorities
 * - Lateral/peer-level alignment across similar time horizons
 */

// ============================================================================
// Time Horizon (Vertical Axis)
// ============================================================================

/**
 * The three time horizons from bottom to top:
 * - get_house_in_order: Near-term, tactical priorities (bottom row)
 * - reinforce_foundation: Connecting layer, building for growth (middle row)  
 * - create_the_future: Long-term, strategic priorities (top row)
 */
export type TimeHorizon = 
  | 'get_house_in_order' 
  | 'reinforce_foundation' 
  | 'create_the_future';

/**
 * Time horizon metadata for display and ordering
 */
export const TIME_HORIZON_CONFIG: Record<TimeHorizon, {
  label: string;
  shortLabel: string;
  description: string;
  focusType: string;
  row: number; // 0 = top, 2 = bottom
}> = {
  create_the_future: {
    label: 'Create the Future',
    shortLabel: 'Future',
    description: 'Long-term strategic priorities pointing toward highest-level objectives',
    focusType: 'Planning',
    row: 0,
  },
  reinforce_foundation: {
    label: 'Reinforce Foundation',
    shortLabel: 'Foundation',
    description: 'Connecting priorities that bridge current needs to future vision',
    focusType: 'Scoping',
    row: 1,
  },
  get_house_in_order: {
    label: 'Get House in Order',
    shortLabel: 'Now',
    description: 'Near-term and immediate priorities addressing current situational needs',
    focusType: 'Executing',
    row: 2,
  },
};

/**
 * Ordered list of time horizons from top to bottom
 */
export const TIME_HORIZONS_ORDERED: TimeHorizon[] = [
  'create_the_future',
  'reinforce_foundation', 
  'get_house_in_order',
];

// ============================================================================
// Strategic Themes (Horizontal Axis)
// ============================================================================

/**
 * Default strategic themes from the book
 */
export const DEFAULT_STRATEGIC_THEMES = [
  'Process Automation',
  'Tech Debt',
  'Defects',
  'Other Priorities',
];

/**
 * A strategic theme column definition
 */
export interface StrategicTheme {
  id: string;
  name: string;
  description?: string;
  color?: string;
}

// ============================================================================
// Pyramid Configuration
// ============================================================================

/**
 * Configuration for a Max Priorities Pyramid instance
 */
export interface PyramidConfig {
  /** Unique identifier for this pyramid configuration */
  id: string;
  
  /** Display name for this pyramid */
  name: string;
  
  /** Column headers (strategic themes) - typically 2-8 columns */
  themes: StrategicTheme[];
  
  /** Time horizons are always the 3 standard rows */
  timeHorizons: typeof TIME_HORIZONS_ORDERED;
  
  /** Associated cycle ID if part of a DEGAP cycle */
  cycleId?: string;
  
  /** Created timestamp */
  createdAt: Date;
  
  /** Last modified timestamp */
  updatedAt: Date;
  
  /** User who created this configuration */
  createdBy: string;
}

/**
 * Create a default pyramid configuration with standard themes
 */
export function createDefaultPyramidConfig(
  name: string,
  createdBy: string,
  customThemes?: string[]
): PyramidConfig {
  const themes = (customThemes || DEFAULT_STRATEGIC_THEMES).map((themeName, idx) => ({
    id: `theme-${idx}`,
    name: themeName,
  }));
  
  return {
    id: `pyramid-${Date.now()}`,
    name,
    themes,
    timeHorizons: TIME_HORIZONS_ORDERED,
    createdAt: new Date(),
    updatedAt: new Date(),
    createdBy,
  };
}

// ============================================================================
// Pyramid Items
// ============================================================================

/**
 * An item placed within the pyramid grid
 */
export interface PyramidItem {
  /** Unique identifier */
  id: string;
  
  /** Item title/name */
  title: string;
  
  /** Optional description */
  description?: string;
  
  /** Time horizon (vertical position) */
  timeHorizon: TimeHorizon;
  
  /** Strategic theme ID (horizontal position) */
  strategicThemeId: string;
  
  /** Strategic theme name for display */
  strategicThemeName: string;
  
  /** Grid position for rendering */
  position: {
    row: number;  // 0-2 (top to bottom)
    col: number;  // 0-N (left to right)
  };
  
  /** Source of this item (miro, excel, manual) */
  source?: 'miro' | 'excel' | 'csv' | 'manual';
  
  /** External ID if imported from Miro */
  externalId?: string;
  
  /** When this item was added */
  addedAt?: Date;
  
  /** Optional rank within the cell */
  rankInCell?: number;
  
  /** Additional metadata from import */
  metadata?: Record<string, unknown>;
}

/**
 * A cell in the pyramid grid
 */
export interface PyramidCell {
  /** Row index (0 = top/future, 2 = bottom/now) */
  row: number;
  
  /** Column index */
  col: number;
  
  /** Time horizon for this row */
  timeHorizon: TimeHorizon;
  
  /** Strategic theme for this column */
  theme: StrategicTheme;
  
  /** Items in this cell */
  items: PyramidItem[];
  
  /** Whether this is the "North Star" cell (top row, typically center or first column) */
  isNorthStar?: boolean;
}

// ============================================================================
// Pyramid Import/Export
// ============================================================================

/**
 * Output structure options when importing a pyramid
 */
export type PyramidOutputStructure = 
  | 'single_list'      // All items in one Focus List with theme+tier as metadata
  | 'per_theme'        // Create one Focus List per strategic theme column
  | 'per_tier';        // Create one Focus List per time horizon row

/**
 * Result of a pyramid import operation
 */
export interface PyramidImportResult {
  /** Whether import was successful */
  success: boolean;
  
  /** The pyramid configuration used */
  config: PyramidConfig;
  
  /** All imported items */
  items: PyramidItem[];
  
  /** Items organized by cell for preview */
  grid: PyramidCell[][];
  
  /** Summary counts */
  summary: {
    totalItems: number;
    byTimeHorizon: Record<TimeHorizon, number>;
    byTheme: Record<string, number>;
  };
  
  /** Any warnings during import */
  warnings?: string[];
  
  /** Error message if failed */
  error?: string;
}

/**
 * Options for exporting pyramid to Focus Lists
 */
export interface PyramidExportOptions {
  /** How to structure the output */
  outputStructure: PyramidOutputStructure;
  
  /** Whether to include time horizon as an attribute on items */
  includeTimeHorizonAttribute: boolean;
  
  /** Whether to include strategic theme as an attribute on items */
  includeThemeAttribute: boolean;
  
  /** Custom name for the focus list(s) */
  listNamePrefix?: string;
  
  /** Associated cycle ID */
  cycleId?: string;
}

// ============================================================================
// Pyramid Alignment Types
// ============================================================================

/**
 * Types of alignment the pyramid helps visualize (from the book)
 */
export type AlignmentType = 
  | 'directional'    // Connection from North Star down through the pyramid
  | 'feedback_loop'  // Operational coherence between tiers
  | 'lateral';       // Peer-level alignment across same time horizon

/**
 * Alignment analysis result for a pyramid
 */
export interface PyramidAlignmentAnalysis {
  /** Overall alignment score (0-100) */
  overallScore: number;
  
  /** Directional alignment - are lower tiers connected to North Star? */
  directionalAlignment: {
    score: number;
    connected: boolean;
    gaps: string[];
  };
  
  /** Feedback loop - can execution flow back to planning? */
  feedbackLoopAlignment: {
    score: number;
    coherent: boolean;
    blockers: string[];
  };
  
  /** Lateral alignment - are peer items at same tier coordinated? */
  lateralAlignment: {
    score: number;
    viable: boolean;
    conflicts: string[];
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the time horizon for a given row index
 */
export function getTimeHorizonByRow(row: number): TimeHorizon {
  return TIME_HORIZONS_ORDERED[row] || 'get_house_in_order';
}

/**
 * Get the row index for a given time horizon
 */
export function getRowByTimeHorizon(horizon: TimeHorizon): number {
  return TIME_HORIZON_CONFIG[horizon].row;
}

/**
 * Validate that a time horizon value is valid
 */
export function isValidTimeHorizon(value: string): value is TimeHorizon {
  return TIME_HORIZONS_ORDERED.includes(value as TimeHorizon);
}

/**
 * Parse a time horizon from various input formats
 */
export function parseTimeHorizon(input: string): TimeHorizon | null {
  const normalized = input.toLowerCase().trim().replace(/[\s_-]+/g, '_');
  
  // Direct match
  if (isValidTimeHorizon(normalized)) {
    return normalized;
  }
  
  // Alias matching
  const aliases: Record<string, TimeHorizon> = {
    'future': 'create_the_future',
    'create_future': 'create_the_future',
    'strategic': 'create_the_future',
    'long_term': 'create_the_future',
    'planning': 'create_the_future',
    
    'foundation': 'reinforce_foundation',
    'reinforce': 'reinforce_foundation',
    'middle': 'reinforce_foundation',
    'scoping': 'reinforce_foundation',
    'growth': 'reinforce_foundation',
    
    'now': 'get_house_in_order',
    'house_in_order': 'get_house_in_order',
    'immediate': 'get_house_in_order',
    'tactical': 'get_house_in_order',
    'executing': 'get_house_in_order',
    'near_term': 'get_house_in_order',
  };
  
  return aliases[normalized] || null;
}

/**
 * Create an empty pyramid grid from a configuration
 */
export function createEmptyPyramidGrid(config: PyramidConfig): PyramidCell[][] {
  const grid: PyramidCell[][] = [];
  
  for (let row = 0; row < 3; row++) {
    const rowCells: PyramidCell[] = [];
    const timeHorizon = TIME_HORIZONS_ORDERED[row];
    
    for (let col = 0; col < config.themes.length; col++) {
      rowCells.push({
        row,
        col,
        timeHorizon,
        theme: config.themes[col],
        items: [],
        isNorthStar: row === 0 && col === 0, // Top-left is North Star by default
      });
    }
    
    grid.push(rowCells);
  }
  
  return grid;
}

/**
 * Place items into a pyramid grid
 */
export function placeItemsInGrid(
  config: PyramidConfig,
  items: PyramidItem[]
): PyramidCell[][] {
  const grid = createEmptyPyramidGrid(config);
  
  for (const item of items) {
    const row = getRowByTimeHorizon(item.timeHorizon);
    const col = config.themes.findIndex(t => 
      t.id === item.strategicThemeId || t.name === item.strategicThemeName
    );
    
    if (row >= 0 && row < 3 && col >= 0 && col < config.themes.length) {
      grid[row][col].items.push(item);
    }
  }
  
  // Sort items within each cell by rankInCell (ascending = highest priority first)
  for (const row of grid) {
    for (const cell of row) {
      cell.items.sort((a, b) => {
        const rankA = a.rankInCell ?? Number.MAX_SAFE_INTEGER;
        const rankB = b.rankInCell ?? Number.MAX_SAFE_INTEGER;
        return rankA - rankB;
      });
    }
  }
  
  return grid;
}

export default {
  TIME_HORIZON_CONFIG,
  TIME_HORIZONS_ORDERED,
  DEFAULT_STRATEGIC_THEMES,
  createDefaultPyramidConfig,
  getTimeHorizonByRow,
  getRowByTimeHorizon,
  isValidTimeHorizon,
  parseTimeHorizon,
  createEmptyPyramidGrid,
  placeItemsInGrid,
};
